<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'

// Small "uppercase label + big number" tile for a row of counters above a list.
// Same shape as admin-web's KpiCard so the two portals' list pages read alike.
// `clickable` turns it into a toggle in a filter-card row; `selected` marks the
// active one.
const props = withDefaults(
  defineProps<{
    label: string
    /** String so callers can pre-format (`'1,234'`). */
    value: string | number
    /** Skeleton in place of the value while data is pending. */
    loading?: boolean
    clickable?: boolean
    selected?: boolean
  }>(),
  { loading: false, clickable: false, selected: false },
)

const emit = defineEmits<{ click: [] }>()

function handleActivate() {
  if (props.clickable) emit('click')
}
</script>

<template>
  <Box
    class="kpi-card"
    :class="{ 'kpi-card--clickable': clickable, 'kpi-card--selected': selected }"
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    :aria-pressed="clickable ? selected : undefined"
    @click="handleActivate"
    @keydown.enter.prevent="handleActivate"
    @keydown.space.prevent="handleActivate"
  >
    <Text
      type="caption-sm"
      transform="uppercase"
      weight="semibold"
      :color="selected ? 'primary' : 'muted'"
      align="center"
      class="kpi-card__label"
    >
      {{ label }}
    </Text>
    <Skeleton v-if="loading" height="36px" width="64px" />
    <Text
      v-else
      type="display"
      weight="bold"
      align="center"
      :color="selected ? 'primary' : 'default'"
    >
      {{ value }}
    </Text>
  </Box>
</template>

<style scoped>
.kpi-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-width: 0;
  padding: 1rem 1.25rem;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
}

.kpi-card--clickable {
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.kpi-card--clickable:hover {
  border-color: var(--p-primary-400);
}

.kpi-card--clickable:focus-visible {
  outline: 2px solid var(--p-primary-400);
  outline-offset: 2px;
}

.kpi-card--selected,
.kpi-card--selected:hover {
  border-color: var(--p-primary-600);
  background: var(--p-primary-50);
  box-shadow: inset 0 0 0 1px var(--p-primary-600);
}

.kpi-card__label {
  max-width: 100%;
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (prefers-reduced-motion: reduce) {
  .kpi-card--clickable {
    transition: none;
  }
}
</style>
